/**
 * Utility per gestione chiavi giocatore canoniche
 * Formato unificato: "PlayerName|TeamName" (con separatore pipe)
 * 
 * Questa utility risolve il problema critico dove il codice
 * usava formati diversi (PlayerName-TeamName vs PlayerName|TeamName)
 * causando mancata corrispondenza tra tag salvati e UI.
 */

import { decodeHtmlEntities } from './decodeHtmlEntities.js';

/**
 * Normalizza una stringa per la chiave (trim + decode entity HTML)
 * @param {string} str - Stringa da normalizzare
 * @returns {string} Stringa normalizzata
 */
function normalizeString(str) {
  if (!str || typeof str !== 'string') return '';
  return decodeHtmlEntities(str).trim();
}

/**
 * Crea una chiave giocatore canonica
 * @param {string} playerName - Nome del giocatore
 * @param {string} teamName - Nome della squadra
 * @returns {string} Chiave canonica nel formato "PlayerName|TeamName"
 */
export function makePlayerKey(playerName, teamName) {
  const name = normalizeString(playerName);
  const team = normalizeString(teamName);
  
  if (!name || !team) {
    console.warn('[PlayerKey] ⚠️ Dati incompleti per generazione chiave:', { playerName, teamName });
    return null;
  }
  
  return `${name}|${team}`;
}

/**
 * Estrae nome e squadra da una chiave giocatore
 * @param {string} playerKey - Chiave nel formato "PlayerName|TeamName"
 * @returns {Object|null} { name, team } o null se formato non valido
 */
export function parsePlayerKey(playerKey) {
  if (!playerKey || typeof playerKey !== 'string') return null;
  
  const parts = playerKey.split('|');
  if (parts.length !== 2) return null;
  
  const [name, team] = parts.map(p => p.trim());
  if (!name || !team) return null;
  
  return { name, team };
}

/**
 * Verifica se una chiave è nel formato canonico
 * @param {string} playerKey - Chiave da verificare
 * @returns {boolean} True se canonica
 */
export function isCanonicalKey(playerKey) {
  if (!playerKey || typeof playerKey !== 'string') return false;
  return parsePlayerKey(playerKey) !== null;
}

/**
 * Migra una chiave legacy (con trattino) al formato canonico
 * @param {string} legacyKey - Chiave nel formato "PlayerName-TeamName"
 * @returns {string|null} Chiave canonica o null se non migrabile
 */
export function migrateLegacyKey(legacyKey) {
  if (!legacyKey || typeof legacyKey !== 'string') return null;
  
  // Se è già canonica, ritorna così com'è
  if (isCanonicalKey(legacyKey)) return legacyKey;
  
  // Prova a migrare da formato con trattino
  const parts = legacyKey.split('-');
  if (parts.length === 2) {
    const [name, team] = parts.map(p => p.trim());
    if (name && team) {
      console.log('[PlayerKey] 🔄 Migrazione chiave legacy:', legacyKey, '→', `${name}|${team}`);
      return `${name}|${team}`;
    }
  }
  
  console.warn('[PlayerKey] ⚠️ Impossibile migrare chiave legacy:', legacyKey);
  return null;
}

/**
 * Normalizza una chiave garantendo formato canonico
 * Supporta retro-compatibilità con chiavi legacy
 * @param {string} playerKey - Chiave da normalizzare
 * @returns {string|null} Chiave canonica
 */
export function normalizePlayerKey(playerKey) {
  if (isCanonicalKey(playerKey)) return playerKey;
  return migrateLegacyKey(playerKey);
}

/**
 * Crea chiave da oggetto giocatore
 * @param {Object} player - Oggetto con proprietà name e team
 * @returns {string|null} Chiave canonica
 */
export function playerToKey(player) {
  if (!player || typeof player !== 'object') return null;
  return makePlayerKey(player.name, player.team);
}

// Debug e utility
/**
 * Analizza un set di chiavi per formati non canonici
 * @param {Array|Set} keys - Collezione di chiavi da analizzare
 * @returns {Object} Statistiche sui formati
 */
export function analyzeKeys(keys) {
  const keyArray = Array.from(keys);
  const stats = {
    total: keyArray.length,
    canonical: 0,
    legacy: 0,
    invalid: 0,
    examples: {
      canonical: [],
      legacy: [],
      invalid: []
    }
  };
  
  keyArray.forEach(key => {
    if (isCanonicalKey(key)) {
      stats.canonical++;
      if (stats.examples.canonical.length < 3) {
        stats.examples.canonical.push(key);
      }
    } else if (migrateLegacyKey(key)) {
      stats.legacy++;
      if (stats.examples.legacy.length < 3) {
        stats.examples.legacy.push(key);
      }
    } else {
      stats.invalid++;
      if (stats.examples.invalid.length < 3) {
        stats.examples.invalid.push(key);
      }
    }
  });
  
  return stats;
}
